export const trendFilter = {
  nav: 'Trend Filter',
  pageTitle: 'Trend Filter',
  common: {
    loadError: 'Could not load this trend run. Try again.',
    backToHistory: 'Back to runs',
    newRun: 'New run',
    viewHistory: 'View runs',
    retry: 'Retry',
  },
  launch: {
    title: 'Trend Opportunity Filter',
    subtitle:
      'Branch seed keywords into rising Google Trends queries, filter the noise against this client’s business context, and surface only actionable opportunities.',
    seeds: {
      label: 'Seed keywords',
      placeholder: 'Type a keyword and press Enter',
      hint: '{{count}} of {{max}} seeds',
      add: 'Add seed',
      removeAria: 'Remove seed {{seed}}',
      duplicate: 'That seed is already added',
    },
    location: {
      label: 'Market',
      placeholder: 'Select a country',
      searchPlaceholder: 'Search country',
      noResults: 'No countries match',
    },
    language: {
      label: 'Language',
    },
    submit: 'Find opportunities',
    submitting: 'Launching…',
    contextGate: {
      title: 'Complete the client context first',
      description:
        'The filter needs the client’s business context to tell relevant trends from noise. Fill in the missing fields, then launch the run.',
      missingFieldsLabel: 'Missing fields',
      cta: 'Go to company settings',
      dismiss: 'Dismiss',
    },
    errors: {
      seedCap: 'You can add at most {{max}} seeds per run',
      atLeastOneSeed: 'Add at least one seed keyword',
      queueUnavailable: 'The processing queue is unavailable right now. Try again in a moment.',
      generic: 'Could not launch the run. Try again.',
    },
  },
  wait: {
    title: 'Finding opportunities',
    subtitle:
      'This usually takes a minute or two. You can leave this page and come back — the run keeps going.',
    leaveHint: 'Safe to leave — we’ll keep processing in the background.',
    phases: {
      branching: {
        label: 'Branching seeds',
        description: 'Expanding seeds into rising Google Trends queries.',
      },
      filtering: {
        label: 'Filtering relevance',
        description: 'Scoring each candidate against the client’s business context.',
      },
      detecting: {
        label: 'Detecting existing content',
        description: 'Checking whether the client already covers each topic.',
      },
    },
    cancel: 'Cancel run',
    cancelling: 'Cancelling…',
    partial: {
      title: 'Finished with partial results',
      description: 'Some seeds failed to process, but we found opportunities from the rest.',
      viewResults: 'View opportunities',
    },
    failed: {
      title: 'The run failed',
      description: 'We couldn’t complete this run. You can start a new one with the same seeds.',
    },
    emptyCompleted: {
      title: 'No actionable signals',
      description: 'The filter ran but found no relevant rising trends for these seeds.',
    },
  },
  dashboard: {
    title: 'Opportunities',
    resultsSummary: '{{kept}} kept of {{total}} candidates',
    searchPlaceholder: 'Search topics',
    columns: {
      topic: 'Topic',
      trend: 'Relative trend',
      reason: 'Why',
      existingContent: 'Already have content?',
      action: 'Recommended action',
      plan: 'Add to plan',
      controls: '',
    },
    trendTooltip: 'Relative position (1–100), not search volume.',
    reasonEmpty: 'No reason provided',
    actionLabels: {
      create: 'Create',
      update: 'Update',
      review: 'Review',
    },
    existingStatusLabels: {
      none: 'No existing content',
      exists: 'Already covered',
      stale: 'Covered but stale',
      unknown: 'Unknown',
      ambiguous: 'Ambiguous match',
    },
    matchedOn: 'Updated {{date}}',
    noMatchDate: 'No date',
    viewContent: 'View content',
    pin: 'Pin',
    unpin: 'Unpin',
    hide: 'Hide',
    unhide: 'Unhide',
    showNoise: 'Show discarded (noise)',
    hideNoise: 'Hide discarded',
    viewStateError: 'Could not update the opportunity. Try again.',
    addToPlan: 'Add to plan',
    viewInPlan: 'In plan',
    addToPlanSuccess: 'Added to “{{plan}}”',
    alreadyInPlan: 'Already in “{{plan}}”',
    addToPlanError: 'Could not add to the content plan. Try again.',
    planPicker: {
      title: 'Choose a content plan',
      description: 'Add “{{topic}}” to which plan?',
      itemCount: '{{count}} items',
    },
    empty: {
      title: 'No actionable signals',
      description:
        'The filter found no relevant opportunities. You can review what was discarded as noise.',
    },
    emptyNoise: {
      title: 'Nothing discarded',
      description: 'No candidates were filtered out as noise for this run.',
    },
  },
  detail: {
    seedLabel: 'From seed “{{seed}}”',
    chartTitle: 'Relative trend',
    chartValueLabel: 'Relative position',
    chartError: 'Could not load the trend series.',
    chartEmpty: 'No trend data for this period.',
    reasonTitle: 'Why this is relevant',
    periods: {
      oneMonth: '1M',
      threeMonths: '3M',
      sixMonths: '6M',
      twelveMonths: '12M',
    },
  },
  history: {
    title: 'Trend runs',
    subtitle: 'Past trend filter runs for this organization.',
    columns: {
      seeds: 'Seeds',
      status: 'Status',
      kept: 'Kept',
      created: 'Started',
      actions: '',
    },
    statusLabels: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      partial: 'Partial',
      failed: 'Failed',
    },
    open: 'Open',
    rerun: 'Re-run same seeds',
    delete: 'Delete',
    deleteConfirm: {
      title: 'Delete this run?',
      description: 'This permanently removes the run and its opportunities. This cannot be undone.',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
    deleteSuccess: 'Run deleted',
    deleteError: 'Could not delete the run. Try again.',
    seedsMore: '+{{count}} more',
    empty: {
      title: 'No runs yet',
      description: 'Launch your first trend filter run to surface rising opportunities.',
      cta: 'New run',
    },
  },
} as const
